/*-
 * #%L
 * Aires Éducatives
 * %%
 * Copyright (C) 2020 - 2023 OFB
 * %%
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 * #L%
 */
import "reflect-metadata"
import { createApp } from "vue"
import App from "./App.vue"
import { initializeLeafletPlugins } from "./leaflet-init"
import router from "./router"
// Buefy 3.0.0 & custom icons
import Buefy from "buefy"
// Import du CSS compilé Buefy après nos styles SCSS personnalisés
import "buefy/dist/css/buefy.css"

// Import du service pour l'initialiser
import { InfoReportingService } from "./services/log/InfoReportingService"

// Configuration FontAwesome externalisée
import { setupFontAwesome, setupFontAwesomeFallback } from "@/config/fontawesome-icons"
import "@/styles/global.scss"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import "quill/dist/quill.core.css"; // import styles
import "quill/dist/quill.snow.css"; // for snow theme
// Validation
// Migration vee-validate v3 -> v4
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import { setupVeeValidate } from "@/services/validation/vee-validation-setup"
import { Field } from "vee-validate"

// Translation
import i18n from "@/i18n"

// Plugin de débogage pour améliorer les logs
import { setupGlobalErrorHandling, setupVueErrorHandling } from "@/plugins/debug-plugin"

const debugLogger = (message: string, ...optionalParams: any[]): void => {
  if (process.env.NODE_ENV === "development") {
    console.log(`[main-init] ${message}`, ...optionalParams)
  }
}

debugLogger("=== DÉBUT INITIALISATION LEAFLET ===")

// Attendre que les plugins Leaflet soient chargés avant d'initialiser Vue
initializeLeafletPlugins()
  .then(() => {
    debugLogger("=== DÉBUT INITIALISATION APPLICATION VUE ===")

    // Vue DevTools en développement
    if (process.env.NODE_ENV === "development") {
      import("@vue/devtools")
    }

    // Configuration FontAwesome
    setupFontAwesome()

    // Créer l'instance Vue
    const app = createApp(App)

    // Configuration des plugins
    app.use(router)
    app.use(Buefy, {
      defaultIconComponent: "vue-fontawesome",
      defaultIconPack: "fas",
    })
    app.use(i18n)

    // Initialiser le service InfoReportingService avec l'instance Vue
    InfoReportingService.setVueApp(app)

    // Enregistrer FontAwesome globalement
    app.component("VueFontawesome", FontAwesomeIcon)

    // Enregistrer les composants vee-validate compatibles (wrappers v3 -> v4)
    app.component("ValidationProvider", ValidationProvider)
    app.component("ValidationObserver", ValidationObserver)
    app.component("VeeField", Field)

    // Configuration de vee-validate v4
    setupVeeValidate()
    setupGlobalErrorHandling()
    setupVueErrorHandling(app)

    // Activer Vue DevTools en développement
    if (process.env.NODE_ENV === "development") {
      app.config.globalProperties.__VUE_DEVTOOLS_GLOBAL_HOOK__ = true
    }
    // Mount l'application
    app.mount("#app")

    // Force le thème clair de Buefy
    document.documentElement.setAttribute("data-theme", "light")

    console.log("=== APPLICATION VUE MONTÉE AVEC SUCCÈS ===")
  })
  .catch((error) => {
    console.error("Erreur critique lors de l'initialisation:", error)
    // Fallback: monter l'application même en cas d'erreur des plugins
    console.warn("Montage de l'application en mode fallback sans plugins Leaflet")
    fallbackAppInit()
  })

function fallbackAppInit() {
  debugLogger("=== INIT FALLBACK APPLICATION ===")

  // InfoReportingService.init() // Commenté temporairement si la méthode n'existe pas

  // Configuration FontAwesome minimale pour le fallback
  setupFontAwesomeFallback()

  const app = createApp(App)
  app.use(router)
  app.use(Buefy, {
    defaultIconComponent: "vue-fontawesome",
    defaultIconPack: "fas",
  })
  app.use(i18n)

  InfoReportingService.setVueApp(app)
  app.component("VueFontawesome", FontAwesomeIcon)
  app.component("ValidationProvider", ValidationProvider)
  app.component("ValidationObserver", ValidationObserver)

  setupVeeValidate()
  setupGlobalErrorHandling()
  setupVueErrorHandling(app)

  app.mount("#app")
  document.documentElement.setAttribute("data-theme", "light")

  console.log("=== APPLICATION FALLBACK MONTÉE ===")
}
